import { HTMLCustomElement } from '@theme/html-custom-element';

/**
 * Adds click-and-drag horizontal scrolling to an overflowing container.
 * Touch devices keep their native momentum scrolling.
 */
const DRAG_THRESHOLD = 4;
const EASE = 0.2;

class DragScroll extends HTMLCustomElement {
  pending = false;
  dragging = false;
  moved = false;
  pointerId = null;
  startX = 0;
  startScroll = 0;
  targetScroll = 0;
  frame = null;

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('pointerdown', this, this.onPointerDown.bind(this));
    this.on('pointermove', this, this.onPointerMove.bind(this));
    this.on('pointerup', this, this.onPointerUp.bind(this));
    this.on('pointercancel', this, this.onPointerUp.bind(this));
    this.on('dragstart', this, (event) => event.preventDefault());
    // Capture phase so a completed drag can cancel the label click (which would
    // otherwise select a variant). A plain click never reaches this state.
    this.on('click', this, this.onClick.bind(this), true);
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();
    this.stopEasing();
  }

  get isOverflowing() {
    return this.scrollWidth > this.clientWidth;
  }

  onPointerDown(event) {
    // Mouse/pen only — touch uses native scrolling.
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (!this.isOverflowing) return;

    this.stopEasing();
    this.pending = true;
    this.dragging = false;
    this.moved = false;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startScroll = this.scrollLeft;
  }

  onPointerMove(event) {
    if (!this.pending) return;

    const delta = event.clientX - this.startX;

    // Only hijack the pointer (and suppress the click) once it's clearly a drag.
    if (!this.dragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      this.dragging = true;
      this.moved = true;
      this.classList.add('is-dragging');
      this.setPointerCapture?.(this.pointerId);
    }

    const max = this.scrollWidth - this.clientWidth;
    this.targetScroll = Math.min(Math.max(this.startScroll - delta, 0), max);
    this.frame ??= requestAnimationFrame(this.ease);
  }

  ease = () => {
    const diff = this.targetScroll - this.scrollLeft;
    const step = diff * EASE;

    // Whole-pixel scroll positions would stall on sub-pixel steps.
    this.scrollLeft += Math.abs(step) < 1 ? diff : step;

    if (Math.abs(this.targetScroll - this.scrollLeft) < 1) {
      this.frame = null;
      return;
    }

    this.frame = requestAnimationFrame(this.ease);
  };

  stopEasing() {
    if (this.frame === null) return;
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  onPointerUp(event) {
    if (!this.pending) return;

    this.pending = false;

    if (this.dragging) {
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.releasePointerCapture?.(event.pointerId);
    }
  }

  onClick(event) {
    if (!this.moved) return;

    event.preventDefault();
    event.stopPropagation();
    this.moved = false;
  }
}

if (!customElements.get('drag-scroll')) {
  customElements.define('drag-scroll', DragScroll);
}
